import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Task } from '../types/Task';
import { PriorityBadge } from './PriorityBadge';
import { TaskActionButtons } from './TaskActionButtons';
import { getTaskPrioritySortValue } from '../utils/taskPriority';
import { formatTaskDate, formatTaskDateTime, isTaskDueDateOverdue } from '../utils/taskDueDate';

const CONTEXT_MENU_IGNORE_SELECTOR = 'button, input, textarea, select, option, a, [role="button"]';
const VIRTUAL_ROW_HEIGHT = 86;
const VIRTUAL_OVERSCAN = 6;
const AUTO_LOAD_MORE_THRESHOLD_PX = VIRTUAL_ROW_HEIGHT * 2;

interface TaskVirtualizedListProps {
  tasks: Task[];
  onDelete: (id: number) => void;
  onStatusChange: (id: number, status: string) => void;
  onViewDetails: (task: Task) => void;
  onEdit: (task: Task) => void;
  onOpenContextMenu?: (task: Task, x: number, y: number) => void;
  selectedTasks: Set<number>;
  onSelectTask: (id: number) => void;
  onSelectAll: () => void;
  canLoadMore?: boolean;
  loadingMoreTasks?: boolean;
  onReachListEnd?: () => void;
}

type SortField = 'title' | 'dueDate' | 'updatedAt' | 'status' | 'priority';
type SortDirection = 'asc' | 'desc';

export const TaskVirtualizedList: React.FC<TaskVirtualizedListProps> = ({
  tasks,
  onDelete,
  onStatusChange,
  onViewDetails,
  onEdit,
  onOpenContextMenu,
  selectedTasks,
  onSelectTask,
  onSelectAll,
  canLoadMore = false,
  loadingMoreTasks = false,
  onReachListEnd,
}) => {
  const [sortField, setSortField] = useState<SortField>('status');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(520);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastAutoLoadTaskCountRef = useRef<number | null>(null);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }

    setSortField(field);
    setSortDirection('asc');
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return ' ↕';
    }

    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const sortedTasks = [...tasks].sort((leftTask, rightTask) => {
    let leftValue: string | number;
    let rightValue: string | number;

    switch (sortField) {
      case 'title':
        leftValue = leftTask.title.toLowerCase();
        rightValue = rightTask.title.toLowerCase();
        break;
      case 'dueDate':
        leftValue = leftTask.dueDate ? new Date(leftTask.dueDate).getTime() : 0;
        rightValue = rightTask.dueDate ? new Date(rightTask.dueDate).getTime() : 0;
        break;
      case 'updatedAt':
        leftValue = leftTask.updatedAt ? new Date(leftTask.updatedAt).getTime() : 0;
        rightValue = rightTask.updatedAt ? new Date(rightTask.updatedAt).getTime() : 0;
        break;
      case 'status': {
        const statusOrder = { TODO: 1, IN_PROGRESS: 2, DONE: 3 };
        leftValue = statusOrder[leftTask.status as keyof typeof statusOrder];
        rightValue = statusOrder[rightTask.status as keyof typeof statusOrder];
        break;
      }
      case 'priority':
        leftValue = getTaskPrioritySortValue(leftTask.priority);
        rightValue = getTaskPrioritySortValue(rightTask.priority);
        break;
      default:
        return 0;
    }

    if (leftValue < rightValue) {
      return sortDirection === 'asc' ? -1 : 1;
    }

    if (leftValue > rightValue) {
      return sortDirection === 'asc' ? 1 : -1;
    }

    return 0;
  });

  const totalRows = sortedTasks.length;
  const startIndex = Math.max(0, Math.floor(scrollTop / VIRTUAL_ROW_HEIGHT) - VIRTUAL_OVERSCAN);
  const visibleRowCount = Math.ceil(containerHeight / VIRTUAL_ROW_HEIGHT) + (VIRTUAL_OVERSCAN * 2);
  const endIndex = Math.min(totalRows, startIndex + visibleRowCount);
  const visibleTasks = sortedTasks.slice(startIndex, endIndex);
  const topSpacerHeight = startIndex * VIRTUAL_ROW_HEIGHT;
  const bottomSpacerHeight = (totalRows - endIndex) * VIRTUAL_ROW_HEIGHT;

  const allSelected = tasks.length > 0 && tasks.every((task) => selectedTasks.has(task.id!));

  const handleRowContextMenu = (event: React.MouseEvent<HTMLTableRowElement>, task: Task) => {
    if (!onOpenContextMenu) {
      return;
    }

    if (event.target instanceof HTMLElement && event.target.closest(CONTEXT_MENU_IGNORE_SELECTOR)) {
      return;
    }

    event.preventDefault();
    onOpenContextMenu(task, event.clientX, event.clientY);
  };

  const handleRowContextMenuKey = (event: React.KeyboardEvent<HTMLTableRowElement>, task: Task) => {
    if (!onOpenContextMenu) {
      return;
    }

    const isKeyboardContextMenu = event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10');
    if (!isKeyboardContextMenu) {
      return;
    }

    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    onOpenContextMenu(task, rect.left + Math.min(40, rect.width / 2), rect.top + Math.min(40, rect.height / 2));
  };

  const maybeRequestMoreTasks = useCallback((container: HTMLDivElement | null) => {
    if (!container || !onReachListEnd || !canLoadMore || loadingMoreTasks) {
      return;
    }

    if (container.clientHeight <= 0 || container.scrollHeight <= 0) {
      return;
    }

    const remainingScroll = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (remainingScroll > AUTO_LOAD_MORE_THRESHOLD_PX) {
      return;
    }

    if (lastAutoLoadTaskCountRef.current === tasks.length) {
      return;
    }

    lastAutoLoadTaskCountRef.current = tasks.length;
    onReachListEnd();
  }, [canLoadMore, loadingMoreTasks, onReachListEnd, tasks.length]);

  useEffect(() => {
    lastAutoLoadTaskCountRef.current = null;
  }, [tasks.length, canLoadMore]);

  useEffect(() => {
    maybeRequestMoreTasks(containerRef.current);
  }, [tasks.length, canLoadMore, loadingMoreTasks, maybeRequestMoreTasks]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    setScrollTop(target.scrollTop);
    setContainerHeight(target.clientHeight);
    maybeRequestMoreTasks(target);
  };

  const formatLabels = (labels: string[] | undefined) => {
    if (!labels || labels.length === 0) {
      return '-';
    }

    return labels.join(', ');
  };

  return (
    <div
      className="table-container table-container-virtualized"
      data-testid="task-virtualized-list"
      ref={containerRef}
      onScroll={handleScroll}
    >
      <table className="task-table task-table-virtualized">
        <thead>
          <tr>
            <th style={{ width: '50px', textAlign: 'center' }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onSelectAll}
                aria-label="Select all tasks"
              />
            </th>
            <th onClick={() => handleSort('title')} className="sortable" style={{ width: '260px' }}>
              Title{getSortIcon('title')}
            </th>
            <th style={{ width: '320px' }}>Description</th>
            <th onClick={() => handleSort('dueDate')} className="sortable" style={{ width: '110px', textAlign: 'center' }}>
              Due Date{getSortIcon('dueDate')}
            </th>
            <th onClick={() => handleSort('updatedAt')} className="sortable" style={{ width: '190px', textAlign: 'center' }}>
              Updated{getSortIcon('updatedAt')}
            </th>
            <th onClick={() => handleSort('priority')} className="sortable" style={{ width: '120px', textAlign: 'center' }}>
              Priority{getSortIcon('priority')}
            </th>
            <th style={{ width: '200px' }}>Labels</th>
            <th onClick={() => handleSort('status')} className="sortable" style={{ width: '115px', textAlign: 'center' }}>
              Status{getSortIcon('status')}
            </th>
            <th style={{ width: '110px', textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedTasks.length > 0 ? (
            <>
              {topSpacerHeight > 0 ? (
                <tr className="virtualized-spacer-row" aria-hidden="true">
                  <td colSpan={9} style={{ height: `${topSpacerHeight}px`, padding: 0, border: 0 }} />
                </tr>
              ) : null}

              {visibleTasks.map((task) => (
                <tr
                  key={task.id!}
                  className={selectedTasks.has(task.id!) ? 'selected-row' : ''}
                  onContextMenu={(event) => handleRowContextMenu(event, task)}
                  onKeyDown={(event) => handleRowContextMenuKey(event, task)}
                  style={{ height: `${VIRTUAL_ROW_HEIGHT}px` }}
                >
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedTasks.has(task.id!)}
                      onChange={() => onSelectTask(task.id!)}
                      onClick={(event) => event.stopPropagation()}
                      aria-label={`Select task: ${task.title}`}
                    />
                  </td>
                  <td className="title-cell">
                    <div className="task-title">{task.title}</div>
                  </td>
                  <td className="description-cell">
                    {task.description ? (
                      <div className="description-collapsed">
                        {task.description.substring(0, 110)}
                        {task.description.length > 110 ? '...' : ''}
                      </div>
                    ) : (
                      <span style={{ color: '#666' }}>-</span>
                    )}
                  </td>
                  <td className="date-cell centered">
                    <span className={isTaskDueDateOverdue(task.dueDate) ? 'task-date-value is-overdue' : 'task-date-value'}>
                      {formatTaskDate(task.dueDate)}
                      {isTaskDueDateOverdue(task.dueDate) ? <span className="task-overdue-badge">Overdue</span> : null}
                    </span>
                  </td>
                  <td className="date-cell centered">{formatTaskDateTime(task.updatedAt)}</td>
                  <td className="centered">
                    <PriorityBadge priority={task.priority} />
                  </td>
                  <td className="labels-cell">{formatLabels(task.labels)}</td>
                  <td className="centered">
                    <select
                      value={task.status}
                      onChange={(event) => onStatusChange(task.id!, event.target.value)}
                      className="status-dropdown"
                    >
                      <option value="TODO">TODO</option>
                      <option value="IN_PROGRESS">In Progress</option>
                      <option value="DONE">Done</option>
                    </select>
                  </td>
                  <td className="centered">
                    <TaskActionButtons
                      onViewDetails={() => onViewDetails(task)}
                      onEdit={() => onEdit(task)}
                      onDelete={() => {
                        if (window.confirm('Are you sure you want to delete this task?')) {
                          onDelete(task.id!);
                        }
                      }}
                    />
                  </td>
                </tr>
              ))}

              {bottomSpacerHeight > 0 ? (
                <tr className="virtualized-spacer-row" aria-hidden="true">
                  <td colSpan={9} style={{ height: `${bottomSpacerHeight}px`, padding: 0, border: 0 }} />
                </tr>
              ) : null}
            </>
          ) : null}
        </tbody>
      </table>
    </div>
  );
};
